import { computed, type Ref } from 'virtual-vue';
import type { PickerOption } from './types';

type ColumnsType = 'plain' | 'multi' | 'cascade';

interface UseColumnsOptions {
  columns: () => PickerOption[] | PickerOption[][];
  internalValues: Ref<(string | number)[]>;
  getFieldName: (_field: string) => string;
  getFieldValue: (_option: PickerOption, _field: string) => string | number;
}

/**
 * Picker 的列类型检测与标准化 composable。
 *
 * 职责：
 * 1. 自动检测 columns 数据格式（plain / multi / cascade）
 * 2. 将级联数据展开为二维数组
 * 3. 统一输出 normalizedColumns 供 PickerColumn 消费
 */
export function useColumns(options: UseColumnsOptions) {
  const { columns, internalValues, getFieldName, getFieldValue } = options;

  /**
   * 自动检测 columns 数据格式：
   * - plain: 一维数组，单列选择
   * - multi: 二维数组，多列独立选择
   * - cascade: 一维数组 + children 字段，级联选择
   */
  const columnsType = computed<ColumnsType>(() => {
    const cols = columns();
    if (!cols || cols.length === 0) return 'plain';
    const firstCol = cols[0];
    if (Array.isArray(firstCol)) return 'multi';
    const childrenKey = getFieldName('children');
    if (firstCol && typeof firstCol === 'object' && childrenKey in firstCol) return 'cascade';
    return 'plain';
  });

  /**
   * 将级联（cascade）数据展开为二维数组
   * 根据当前 internalValues 逐层匹配选中项，取其 children 作为下一列
   */
  const formatCascadeColumns = (): PickerOption[][] => {
    const result: PickerOption[][] = [];
    let childrenOptions = columns() as PickerOption[];
    let depth = 0;
    const childrenKey = getFieldName('children');
    while (childrenOptions && childrenOptions.length > 0) {
      result.push(childrenOptions);
      const selectedValue = internalValues.value[depth];
      const selected = childrenOptions.find(
        opt => getFieldValue(opt, 'value') === selectedValue,
      ) || childrenOptions[0];
      childrenOptions = selected ? selected[childrenKey] as PickerOption[] : undefined as unknown as PickerOption[];
      depth += 1;
    }
    return result;
  };

  /**
   * 标准化后的列数据，统一为 PickerOption[][] 格式
   * 无论传入的是单列、多列还是级联，内部一律按二维数组处理
   */
  const normalizedColumns = computed<PickerOption[][]>(() => {
    const cols = columns();
    if (!cols || cols.length === 0) return [];
    switch (columnsType.value) {
      case 'multi':
        return cols as PickerOption[][];
      case 'cascade':
        return formatCascadeColumns();
      default:
        return [cols as PickerOption[]];
    }
  });

  return {
    columnsType,
    normalizedColumns,
    formatCascadeColumns,
  };
}
